<template>
  <div class="category-databank">
    <nav class="vp-crumbs" :aria-label="$t('databank.category.title')">
      <router-link to="/databank">{{ $t('databank.title') }}</router-link>
      <span class="vp-crumbs-sep">/</span>
      <span>{{ $t('databank.category.title') }}</span>
    </nav>

    <div class="vp-page-head">
      <div class="vp-min-w-0">
        <h1 class="vp-page-title">{{ $t('databank.category.title') }}</h1>
        <div class="vp-page-sub">
          <i class="ph-duotone ph-list-tree"></i>
          <span>{{ $t('databank.category.description') }}</span>
        </div>
      </div>
      <div class="vp-page-actions">
        <button type="button" class="vp-btn vp-btn-ghost" :disabled="isLoadingData" @click="loadRootCategories()">
          <i class="ph-duotone ph-arrow-clockwise" :class="{ 'vp-spin': isLoadingData }"></i>
          {{ $t('common.refresh') }}
        </button>
        <button type="button" class="vp-btn vp-btn-primary" @click="openAddModal">
          <i class="ph-duotone ph-plus"></i>{{ $t('databank.category.addTitle') }}
        </button>
      </div>
    </div>

    <div v-if="error" class="vp-note vp-note--danger vp-stack-bottom">
      <i class="ph-duotone ph-warning-circle"></i>
      <div class="vp-min-w-0">{{ error }}</div>
      <button type="button" class="vp-note-dismiss" :aria-label="$t('common.close')" @click="error = null">
        <i class="ph-duotone ph-x"></i>
      </button>
    </div>

    <div v-if="success" class="vp-note vp-note--success vp-stack-bottom">
      <i class="ph-duotone ph-check-circle"></i>
      <div class="vp-min-w-0">{{ success }}</div>
      <button type="button" class="vp-note-dismiss" :aria-label="$t('common.close')" @click="success = null">
        <i class="ph-duotone ph-x"></i>
      </button>
    </div>

    <div class="vp-filterbar">
      <div class="vp-field vp-field--grow">
        <i class="ph-duotone ph-magnifying-glass vp-field-icon"></i>
        <input
          v-model="searchQuery"
          type="search"
          class="vp-input"
          :placeholder="$t('common.actions.search')"
          :aria-label="$t('common.actions.search')"
        />
      </div>
      <div class="vp-filterbar-end">
        <button
          v-if="searchQuery"
          type="button"
          class="vp-btn vp-btn-ghost vp-btn-sm"
          :title="$t('common.clear')"
          @click="clearSearch"
        >
          <i class="ph-duotone ph-x"></i>{{ $t('common.clear') }}
        </button>
      </div>
    </div>

    <section class="vp-card">
      <div class="vp-card-head">
        <h6 class="vp-card-title">
          {{ $t('databank.category.existingRecords') }}
          <span class="vp-chip">{{ $formatInteger(flatRows.length) }}</span>
        </h6>
      </div>

      <div v-if="isLoadingData" class="vp-loading"><span class="spinner-border spinner-border-sm"></span></div>

      <div v-else-if="flatRows.length" class="vp-table-wrap">
        <table class="vp-table" :aria-label="$t('databank.category.title')">
          <thead>
            <tr>
              <th>{{ $t('databank.category.code') }}</th>
              <th>{{ $t('databank.category.name') }}</th>
              <th>{{ $t('databank.category.parent') }}</th>
              <th>{{ $t('common.createdAt') }}</th>
              <th class="text-end">{{ $t('common.actionsLabel') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in flatRows" :key="row.id">
              <td>
                <div class="tree-cell" :style="{ paddingLeft: `${row.depth * 18}px` }">
                  <button
                    v-if="row.isHaveChild"
                    type="button"
                    class="tree-toggle"
                    :aria-expanded="isExpanded(row.id)"
                    :title="row.name"
                    @click="toggleExpand(row)"
                  >
                    <i :class="isExpanded(row.id) ? 'ph-duotone ph-caret-down' : 'ph-duotone ph-caret-right'"></i>
                  </button>
                  <span v-else class="tree-spacer"></span>
                  <span v-if="row.code" class="vp-mono">{{ row.code }}</span>
                  <span v-else class="vp-text-muted">—</span>
                </div>
              </td>
              <td class="vp-cell-strong">{{ row.name }}</td>
              <td>
                <span v-if="row.parentName">{{ row.parentName }}</span>
                <span v-else class="vp-text-muted">—</span>
              </td>
              <td class="vp-num text-nowrap">{{ formatDate(row.createdAt) }}</td>
              <td class="text-end">
                <div class="vp-row-actions">
                  <button
                    type="button"
                    class="vp-icon-btn"
                    :title="$t('common.edit')"
                    @click="openEditModal(row)"
                  >
                    <i class="ph-duotone ph-pencil-simple"></i>
                  </button>
                  <button
                    type="button"
                    class="vp-icon-btn vp-icon-btn--danger"
                    :title="$t('common.delete')"
                    @click="deleteCategory(row)"
                  >
                    <i class="ph-duotone ph-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="vp-empty">
        <i class="ph-duotone ph-list-tree vp-empty-icon"></i>
        <div class="vp-empty-title">{{ $t('databank.category.title') }}</div>
        <div class="vp-empty-text">{{ $t('databank.category.noRecords') }}</div>
      </div>
    </section>

    <!-- Add / edit category -->
    <div id="categoryModal" class="modal fade" tabindex="-1" aria-labelledby="categoryModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content vp-modal">
          <div class="vp-modal-head">
            <h5 id="categoryModalLabel" class="vp-modal-title">
              <i class="ph-duotone ph-list-tree"></i>
              {{ isEditing ? $t('databank.category.editTitle') : $t('databank.category.addTitle') }}
            </h5>
            <button type="button" class="vp-modal-close" data-bs-dismiss="modal" :aria-label="$t('common.close')">
              <i class="ph-duotone ph-x"></i>
            </button>
          </div>

          <div class="vp-modal-body">
            <form class="vp-form-grid" @submit.prevent>
              <div class="vp-form-field vp-form-field--full">
                <CategoryCascader
                  v-model="form.parentId"
                  :label="$t('databank.category.parent')"
                  :help-text="$t('databank.category.parentHelp')"
                  :disabled-category-id="isEditing ? editingId : ''"
                />
                <button
                  v-if="form.parentId"
                  type="button"
                  class="vp-btn vp-btn-ghost vp-btn-sm"
                  @click="form.parentId = ''"
                >
                  <i class="ph-duotone ph-x"></i>{{ $t('databank.category.clearParent') }}
                </button>
              </div>

              <div class="vp-form-field vp-form-field--full">
                <label class="vp-label" for="categoryName">
                  {{ $t('databank.category.name') }} <span class="vp-label-req">*</span>
                </label>
                <input
                  id="categoryName"
                  v-model="form.name"
                  type="text"
                  class="vp-control"
                  :maxlength="MATERIAL_CATEGORY_MAX.name"
                  :placeholder="$t('databank.category.namePlaceholder')"
                />
              </div>

              <div class="vp-form-field vp-form-field--full">
                <label class="vp-label" for="categoryCode">
                  {{ $t('databank.category.code') }} <span class="vp-label-req">*</span>
                </label>
                <input
                  id="categoryCode"
                  v-model="form.code"
                  type="text"
                  class="vp-control vp-mono"
                  :maxlength="MATERIAL_CATEGORY_MAX.code"
                  :placeholder="$t('databank.category.codePlaceholder')"
                />
              </div>

              <div class="vp-form-field vp-form-field--full">
                <label class="vp-label" for="categoryDescription">
                  {{ $t('databank.category.descriptionField') }}
                </label>
                <textarea
                  id="categoryDescription"
                  v-model="form.description"
                  rows="3"
                  class="vp-control"
                  :maxlength="MATERIAL_CATEGORY_MAX.description"
                  :placeholder="$t('databank.category.descriptionPlaceholder')"
                ></textarea>
              </div>
            </form>
          </div>

          <div class="vp-modal-foot">
            <button type="button" class="vp-btn vp-btn-primary" :disabled="isSaving" @click="saveCategory">
              <span v-if="isSaving" class="spinner-border spinner-border-sm"></span>
              <i v-else class="ph-duotone ph-check"></i>
              {{ isSaving ? $t('common.saving') : $t('common.saveAndClose') }}
            </button>
            <button type="button" class="vp-btn vp-btn-ghost" data-bs-dismiss="modal">
              {{ $t('common.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Modal } from 'bootstrap';
import CategoryCascader from '@/components/profile/CategoryCascader.vue';
import materialCategoryService from '@/services/materialCategoryService';
import { MATERIAL_CATEGORY_MAX } from '@/constants/maxLengths';

const { t } = useI18n();

const isLoadingData = ref(false);
const isSaving = ref(false);
const isEditing = ref(false);
const editingId = ref('');
const searchQuery = ref('');
const error = ref(null);
const success = ref(null);
const categories = ref([]);
const expandedIds = ref(new Set());

const form = ref({
  name: '',
  code: '',
  description: '',
  parentId: ''
});

let categoryModal = null;
let searchTimeout = null;

const flatRows = computed(() => {
  const rows = [];
  const visit = (nodes, depth, parentName = '') => {
    for (const node of nodes) {
      rows.push({ ...node, depth, parentName });
      if (expandedIds.value.has(node.id) && node.children?.length) {
        visit(node.children, depth + 1, node.name);
      }
    }
  };
  visit(categories.value, 0, '');
  return rows;
});

function normalizeCategory(item) {
  return {
    id: item.id,
    code: item.code || '',
    name: item.name || '',
    description: item.description || '',
    parentId: item.parentId || '',
    isHaveChild: item.isHaveChild === true,
    createdAt: item.creationTime || item.createdAt || null,
    children: Array.isArray(item.children) ? item.children : [],
    childrenLoaded: item.childrenLoaded === true
  };
}

function formatDate(date) {
  if (!date) return '-';
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
}

function isExpanded(id) {
  return expandedIds.value.has(id);
}

async function loadRootCategories() {
  isLoadingData.value = true;
  error.value = null;
  try {
    const response = await materialCategoryService.searchCategories({
      searchText: searchQuery.value.trim() || '',
      page: 1,
      pageSize: 100,
      sorting: 'name asc',
      parentId: null
    });
    const items = Array.isArray(response?.items) ? response.items : [];
    categories.value = items.map(normalizeCategory);
    expandedIds.value = new Set();
  } catch (err) {
    error.value = t('databank.category.errors.loadError');
  } finally {
    isLoadingData.value = false;
  }
}

async function loadChildren(row) {
  const target = findCategory(categories.value, row.id);
  if (!target || target.childrenLoaded) return;
  const children = await materialCategoryService.getChildCategories(target.id);
  target.children = (Array.isArray(children) ? children : []).map(normalizeCategory);
  target.childrenLoaded = true;
}

async function toggleExpand(row) {
  if (!isExpanded(row.id)) {
    try {
      await loadChildren(row);
      expandedIds.value.add(row.id);
    } catch (err) {
      error.value = t('databank.category.errors.loadChildrenError');
    }
    return;
  }
  expandedIds.value.delete(row.id);
}

function findCategory(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children?.length) {
      const found = findCategory(n.children, id);
      if (found) return found;
    }
  }
  return null;
}

function clearForm() {
  form.value = {
    name: '',
    code: '',
    description: '',
    parentId: ''
  };
  isEditing.value = false;
  editingId.value = '';
}

function openAddModal() {
  clearForm();
  categoryModal?.show();
}

async function openEditModal(category) {
  isEditing.value = true;
  editingId.value = category.id;
  form.value = {
    name: category.name || '',
    code: category.code || '',
    description: category.description || '',
    parentId: category.parentId || ''
  };
  categoryModal?.show();
}

function validateForm() {
  if (!form.value.name.trim()) {
    error.value = t('databank.category.errors.nameRequired');
    return false;
  }
  if (!form.value.code.trim()) {
    error.value = t('databank.category.errors.codeRequired');
    return false;
  }
  return true;
}

function extractApiErrorMessage(err, fallbackMessage) {
  const responseData = err?.response?.data;
  const apiError = responseData?.error;

  const validationMessages = Array.isArray(apiError?.validationErrors)
    ? apiError.validationErrors
        .map((v) => v?.message)
        .filter((m) => typeof m === 'string' && m.trim().length > 0)
    : [];

  if (validationMessages.length > 0) {
    return validationMessages.join(' ');
  }

  if (typeof apiError?.details === 'string' && apiError.details.trim()) {
    return apiError.details.trim();
  }

  if (typeof apiError?.message === 'string' && apiError.message.trim()) {
    return apiError.message.trim();
  }

  if (typeof responseData?.message === 'string' && responseData.message.trim()) {
    return responseData.message.trim();
  }

  if (typeof err?.message === 'string' && err.message.trim()) {
    return err.message.trim();
  }

  return fallbackMessage;
}

async function saveCategory() {
  if (!validateForm()) return;
  isSaving.value = true;
  error.value = null;
  success.value = null;
  try {
    const payload = {
      name: form.value.name.trim(),
      code: form.value.code.trim(),
      description: form.value.description?.trim() || null,
      parentId: form.value.parentId || null
    };

    if (isEditing.value) {
      await materialCategoryService.updateCategory(editingId.value, payload);
      success.value = t('databank.category.updateSuccess');
    } else {
      await materialCategoryService.createCategory(payload);
      success.value = t('databank.category.createSuccess');
    }

    await loadRootCategories();
    categoryModal?.hide();
    clearForm();
  } catch (err) {
    error.value = extractApiErrorMessage(err, t('databank.category.errors.saveError'));
  } finally {
    isSaving.value = false;
  }
}

async function deleteCategory(category) {
  const confirmed = window.confirm(
    `${t('databank.category.confirmDelete')}\n${category.code} - ${category.name}`
  );
  if (!confirmed) return;
  error.value = null;
  success.value = null;
  try {
    await materialCategoryService.deleteCategory(category.id);
    success.value = t('databank.category.deleteSuccess');
    await loadRootCategories();
  } catch (err) {
    error.value = extractApiErrorMessage(err, t('databank.category.errors.deleteError'));
  }
}

function clearSearch() {
  searchQuery.value = '';
}

watch(searchQuery, () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    loadRootCategories();
  }, 400);
});

onMounted(() => {
  const modalElement = document.getElementById('categoryModal');
  if (modalElement) {
    categoryModal = new Modal(modalElement, { focus: false });
  }
  loadRootCategories();
});
</script>

<style scoped>
/* Depth is expressed by indent, so the caret column has to hold its width even
   when a leaf row has no caret to show. */
.tree-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tree-toggle {
  width: 16px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--vp-gray-500);
  cursor: pointer;
}

.tree-toggle:hover {
  color: var(--vp-primary);
}

.tree-spacer {
  width: 16px;
  flex-shrink: 0;
}
</style>
